import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Megaphone, FileText, Download } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const WorkerToolboxTalks = ({ user }) => {
  const { t } = useTranslation();
  const [talks, setTalks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadToolboxTalks = useCallback(async () => {
    setLoading(true);
    try {
      // Load only assigned toolbox talks (sent by admin)
      const response = await axiosInstance.get('/my-toolbox-talks');
      setTalks(response.data);
    } catch (error) {
      console.error('Failed to load toolbox talks:', error);
      toast.error(t('toolbox.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadToolboxTalks();
  }, [loadToolboxTalks]);

  const downloadPdf = async (talkId, title) => {
    try {
      const response = await axiosInstance.get(`/toolbox-talks/${talkId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('toolbox.pdfDownloaded'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(t('toolbox.failedPdfDownload'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
        >
          ← {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('toolbox.title')}</h1>
          <p className="text-gray-600 mt-1">{t('toolbox.safetyTalksDesc')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        ) : talks.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Megaphone className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('toolbox.noAssignedTalks')}</h3>
              <p className="text-slate-500">{t('toolbox.willAppearWhenAvailable')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {talks.map(talk => (
              <Card key={talk.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Megaphone className="w-6 h-6 text-teal-600 mt-1" />
                      <div>
                        <CardTitle className="text-xl">{talk.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {talk.description}
                        </CardDescription>
                      </div>
                    </div>
                    {talk.pdfPath && (
                      <Badge variant="outline" className="bg-teal-50 text-teal-700">
                        <FileText className="w-3 h-3 mr-1" />
                        PDF
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {talk.content && (
                    <div className="bg-slate-50 p-4 rounded-lg mb-4">
                      <p className="text-slate-700 whitespace-pre-wrap">{talk.content}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Created: {new Date(talk.createdAt).toLocaleDateString()}
                    </p>
                    
                    {talk.pdfPath && (
                      <Button
                        onClick={() => downloadPdf(talk.id, talk.title)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerToolboxTalks;